import { PageFormat } from '@ritabygglov/core';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useUser } from '../context/User/useUser';
import { exportViewBox, initialViewBox, ViewBox } from '../types';
import { useRoomId as useRoomIdImport } from './useRoomId';
import useWindowDimensions from './useWindowDimensions';

const isWindowDimensionsUpdated = (
  w1: ReturnType<typeof useWindowDimensions>,
  w2: ReturnType<typeof useWindowDimensions>
) => {
  return w1.height !== w2.height || w1.width !== w2.width;
};

export const useViewBox = (
  useRoomId = useRoomIdImport
): [ViewBox, React.Dispatch<React.SetStateAction<ViewBox>>] => {
  const user = useUser();
  const canvasId = useRoomId();
  const canvas = user.canvases.find((c) => c.canvasId === canvasId);

  const [hasBootstrapped, setHasBootstraped] = useState(false);
  const location = useLocation();
  const isExport = location.pathname.includes('export');
  const startingViewBox = isExport
    ? exportViewBox(canvas?.pageFormat || PageFormat.A3_LANDSCAPE)
    : initialViewBox(window, canvas?.pageFormat || PageFormat.A3_LANDSCAPE);

  const [viewBox, setViewBox] = useState<ViewBox>(startingViewBox);
  const currentWindowDimension = useWindowDimensions();

  const [windowDimension, setWindowDimensions] = useState(
    currentWindowDimension
  );

  useEffect(() => {
    if (!hasBootstrapped && canvas) {
      setViewBox(startingViewBox);
      setHasBootstraped(true);
    }
  }, [canvas, hasBootstrapped, startingViewBox]);

  useLayoutEffect(() => {
    // This will adjust the viewBox when the window size changes
    if (isWindowDimensionsUpdated(windowDimension, currentWindowDimension)) {
      if (!isExport) {
        const diffH = windowDimension.height - currentWindowDimension.height;
        const diffW = windowDimension.width - currentWindowDimension.width;

        setViewBox((vB) => {
          const w = viewBox.w - diffW * viewBox.scale;
          return {
            ...vB,
            h: viewBox.h - diffH * viewBox.scale,
            w,
            // Keep scale consistent with w, which the zoom UI reads back.
            scale: w / currentWindowDimension.width,
          };
        });
      }
      setWindowDimensions(currentWindowDimension);
    }
  }, [viewBox, setViewBox, currentWindowDimension, windowDimension, isExport]);

  return [viewBox, setViewBox];
};
